import { useState, useEffect, useCallback, useMemo } from 'react';
import { Clock, CheckCircle, XCircle, Archive, Building2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import { API_URL, GED_ENTITIES, ICON_MAP, getFileTypeInfo, canPreviewInline, gedDownload, getViewUrl, CATEGORY_ENTITY_MAP } from './constants';
import { getToken } from '../../../services/tokenService';

export default function useGEDESS(userRole = 'member') {
  const [selectedEntity, setSelectedEntity] = useState('coppam');
  const [documents, setDocuments] = useState([]);
  const [documentsByEntity, setDocumentsByEntity] = useState({});
  const [souscriptionDocuments, setSouscriptionDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory] = useState('');
  const [showSouscriptionDocs, setShowSouscriptionDocs] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [activePanel, setActivePanel] = useState('documents');
  const [connectedData, setConnectedData] = useState({ membres: [], ftpe_adhesions: [], simulations_rcds: [], avenants: [] });
  const [connectedCounts, setConnectedCounts] = useState({});
  const [connectedLoading, setConnectedLoading] = useState(false);
  const [connectedSearch, setConnectedSearch] = useState('');
  const [docFilters, setDocFilters] = useState({});
  const [membresFilters, setMembresFilters] = useState({});
  const [ftpeFilters, setFtpeFilters] = useState({});
  const [simFilters, setSimFilters] = useState({});
  const [customCategories, setCustomCategories] = useState({});
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [avenantSearch, setAvenantSearch] = useState('');
  const [avenantFilters, setAvenantFilters] = useState({});
  const [complianceData, setComplianceData] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditFilters, setAuditFilters] = useState({});
  const [customEntities, setCustomEntities] = useState([]);
  const [showAddEntity, setShowAddEntity] = useState(false);
  const [newEntityForm, setNewEntityForm] = useState({ name: '', description: '', badge: '', badge_color: 'bg-cyan-600' });
  const [showInlineNewCat, setShowInlineNewCat] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [publishingDoc, setPublishingDoc] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragFiles, setDragFiles] = useState([]);
  const [dragPreviews, setDragPreviews] = useState([]);
  const [printingDocId, setPrintingDocId] = useState(null);
  const [uploadForm, setUploadForm] = useState({ file: null, entite: '', categorie: '', description: '', tags: '' });

  const getAuthHeaders = () => {
    const token = getToken();
    return { 'Authorization': `Bearer ${token}` };
  };

  useEffect(() => {
    if (selectedEntity) loadDocuments(selectedEntity);
  }, [selectedEntity, searchQuery, selectedCategory]);

  useEffect(() => {
    loadAllEntitiesCounts();
    loadSouscriptionDocuments();
    loadConnectedSources();
    loadCustomCategories();
    loadCompliance();
    loadAuditJournal();
    loadCustomEntities();
  }, []);

  const logAuditAction = async (actionType, actionLabel, entity = '', entityId = '', metadata = {}) => {
    try {
      const formData = new FormData();
      formData.append('action_type', actionType);
      formData.append('action_label', actionLabel);
      formData.append('entity', entity);
      formData.append('entity_id', entityId);
      formData.append('source', 'ged_ess');
      formData.append('metadata_json', JSON.stringify(metadata));
      await fetch(`${API_URL}/api/audit/log`, { method: 'POST', headers: getAuthHeaders(), body: formData });
    } catch (err) { console.error('Audit log error:', err); }
  };

  const loadCompliance = async () => {
    try {
      const res = await fetch(`${API_URL}/api/audit/compliance`, { headers: getAuthHeaders() });
      if (res.ok) setComplianceData(await res.json());
    } catch (err) { console.error('Compliance error:', err); }
  };

  const loadAuditJournal = async () => {
    try {
      const res = await fetch(`${API_URL}/api/audit/journal?limit=100`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAuditEntries(data.entries || []);
      }
    } catch (err) { console.error('Audit journal error:', err); }
  };

  const loadCustomEntities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ged/entities`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCustomEntities(data.entities || []);
      }
    } catch (err) { console.error('Custom entities error:', err); }
  };

  const handleCreateEntity = async () => {
    if (!newEntityForm.name.trim()) return;
    try {
      const fd = new FormData();
      fd.append('name', newEntityForm.name.trim());
      fd.append('description', newEntityForm.description);
      fd.append('badge', newEntityForm.badge || newEntityForm.name.toUpperCase());
      fd.append('badge_color', newEntityForm.badge_color);
      const res = await fetch(`${API_URL}/api/ged/entities`, { method: 'POST', headers: getAuthHeaders(), body: fd });
      if (res.ok) {
        setNewEntityForm({ name: '', description: '', badge: '', badge_color: 'bg-cyan-600' });
        setShowAddEntity(false);
        await loadCustomEntities();
        setSuccess('Entite creee avec succes');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Erreur creation entite');
        setTimeout(() => setError(''), 4000);
      }
    } catch { setError('Erreur reseau'); }
  };

  const handlePublishToRegistry = async (doc) => {
    setPublishingDoc(doc.id);
    try {
      const res = await fetch(`${API_URL}/api/registry/publish`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          doc_uuid: doc.id,
          doc_type: doc.categorie || doc.type || 'document',
          issuer: doc.entite ? (GED_ENTITIES[doc.entite]?.name || doc.entite) : 'ESS',
          file_path: doc.file_path || doc.path,
          version: '1.0',
          metadata: { nom: doc.original_filename || doc.nom, entite: doc.entite }
        })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(`Document publie au registre: ${data.registry_id}`);
        loadDocuments(selectedEntity);
      } else {
        setError(data.detail || 'Erreur publication');
      }
    } catch { setError('Erreur reseau lors de la publication'); }
    finally { setPublishingDoc(null); setTimeout(() => { setSuccess(''); setError(''); }, 5000); }
  };

  const handleInlineCreateCategory = async () => {
    if (!newCatLabel.trim() || !uploadForm.entite) return;
    try {
      const fd = new FormData();
      fd.append('label', newCatLabel.trim());
      fd.append('connected', false);
      const res = await fetch(`${API_URL}/api/ged/categories/${uploadForm.entite}`, { method: 'POST', headers: getAuthHeaders(), body: fd });
      if (res.ok) {
        const data = await res.json();
        await loadCustomCategories();
        setUploadForm(prev => ({ ...prev, categorie: data.category?.value || '' }));
        setNewCatLabel('');
        setShowInlineNewCat(false);
      } else {
        const err = await res.json();
        setError(err.detail || 'Erreur creation categorie');
      }
    } catch { setError('Erreur reseau'); }
  };

  const allEntities = useMemo(() => {
    const hardcoded = Object.values(GED_ENTITIES);
    const custom = customEntities.map(ce => ({
      ...ce,
      icon: ICON_MAP[ce.icon] || Building2,
      categories: ce.categories || [],
      is_custom: true,
    }));
    return [...hardcoded, ...custom];
  }, [customEntities]);

  const loadCustomCategories = async () => {
    try {
      const cats = {};
      const entityIds = [...Object.keys(GED_ENTITIES), ...customEntities.map(e => e.id)];
      for (const entityId of entityIds) {
        const res = await fetch(`${API_URL}/api/ged/categories/${entityId}`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          cats[entityId] = data.categories || [];
        }
      }
      setCustomCategories(cats);
    } catch {
    }
  };

  const getMergedCategories = useCallback((entityId) => {
    const defaults = GED_ENTITIES[entityId]?.categories || [];
    const custom = (customCategories[entityId] || []).map(c => ({
      value: c.value, label: c.label, is_custom: true, connected: c.connected, id: c.id
    }));
    return [...defaults, ...custom];
  }, [customCategories]);

  const filteredDocuments = useMemo(() => {
    let docs = [...documents];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      docs = docs.filter(d =>
        (d.original_filename || d.nom || '').toLowerCase().includes(q) ||
        (d.categorie || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q) ||
        (d.user_nom || '').toLowerCase().includes(q) ||
        (d.user_prenom || '').toLowerCase().includes(q)
      );
    }
    if (docFilters.categorie) docs = docs.filter(d => d.categorie === docFilters.categorie);
    if (docFilters.statut) docs = docs.filter(d => d.statut === docFilters.statut);
    if (docFilters.type_fichier) {
      docs = docs.filter(d => {
        const info = getFileTypeInfo(d.original_filename || d.filename, d.mime_type);
        return info.type === docFilters.type_fichier;
      });
    }
    if (docFilters.periode) {
      const days = parseInt(docFilters.periode);
      const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      docs = docs.filter(d => new Date(d.uploaded_at) >= cutoff);
    }
    return docs;
  }, [documents, searchQuery, docFilters]);

  const loadConnectedSources = async () => {
    setConnectedLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ged/connected-sources`, { headers: getAuthHeaders() });
      if (response.ok) {
        const result = await response.json();
        setConnectedData(result.data || {});
        setConnectedCounts(result.counts || {});
      }
    } catch {
    } finally {
      setConnectedLoading(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setDragFiles(files);
      const previews = files.map(file => {
        const info = getFileTypeInfo(file.name, file.type);
        return { file, info, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null };
      });
      setDragPreviews(previews);
      setUploadForm(prev => ({ ...prev, file: files[0], entite: selectedEntity }));
      setShowUploadModal(true);
    }
  };

  const handleDragUpload = async (entite, categorie, description) => {
    if (dragFiles.length === 0 || !entite || !categorie) return;
    setUploading(true);
    setError('');
    let successCount = 0;
    try {
      for (const file of dragFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entite', entite);
        formData.append('categorie', categorie);
        if (description) formData.append('description', description);
        const response = await fetch(`${API_URL}/api/ged/upload`, {
          method: 'POST', headers: getAuthHeaders(), body: formData
        });
        if (response.ok) successCount++;
      }
      if (successCount > 0) {
        setSuccess(`${successCount} document(s) uploade(s) avec succes !`);
        setShowUploadModal(false);
        setDragFiles([]);
        setDragPreviews(prev => { prev.forEach(p => p.preview && URL.revokeObjectURL(p.preview)); return []; });
        setUploadForm({ file: null, entite: '', categorie: '', description: '', tags: '' });
        loadDocuments(selectedEntity);
        loadAllEntitiesCounts();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const loadSouscriptionDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ged/souscription-documents`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSouscriptionDocuments(data.documents || []);
      }
    } catch {
    }
  };

  const loadAllEntitiesCounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ged/documents?limit=1000`, { headers: getAuthHeaders() });
      if (response.ok) {
        const result = await response.json();
        const docs = result.data || result;
        const grouped = {};
        Object.keys(GED_ENTITIES).forEach(key => {
          grouped[key] = (docs || []).filter(d => {
            const docEntite = d.entite || CATEGORY_ENTITY_MAP[d.categorie] || '';
            return docEntite === key;
          }).length;
        });
        customEntities.forEach(ce => {
          grouped[ce.id] = (docs || []).filter(d => d.entite === ce.id).length;
        });
        setDocumentsByEntity(grouped);
      }
    } catch {
    }
  };

  const loadDocuments = async (entityId) => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/ged/documents?entite=${entityId}`;
      if (selectedCategory) url += `&categorie=${selectedCategory}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const result = await response.json();
        const docs = result.data || result;
        setDocuments(Array.isArray(docs) ? docs.sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0)) : []);
      } else {
        setDocuments([]);
      }
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.entite || !uploadForm.categorie) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('entite', uploadForm.entite);
      formData.append('categorie', uploadForm.categorie);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);
      const response = await fetch(`${API_URL}/api/ged/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      if (response.ok) {
        setSuccess('Document uploade avec succes !');
        logAuditAction('upload', 'Upload document GED', selectedEntity, '', { filename: uploadForm.file?.name });
        setShowUploadModal(false);
        setUploadForm({ file: null, entite: '', categorie: '', description: '', tags: '' });
        loadDocuments(selectedEntity);
        loadAllEntitiesCounts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors de l\'upload');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleView = (doc) => {
    const filename = doc.original_filename || doc.filename || doc.nom;
    logAuditAction('view', `Consultation document: ${filename}`, selectedEntity, doc.id);
    const previewable = canPreviewInline(filename, doc.mime_type);
    if (previewable) {
      setPreviewDoc(doc);
    } else if (doc.souscription_id) {
      const token = getToken();
      window.open(`${API_URL}/api/souscriptions-rcd/${doc.souscription_id}/documents/rcds_unique?token=${token}`, '_blank');
    } else {
      window.open(getViewUrl(doc.id), '_blank');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    logAuditAction('delete', 'Suppression document GED', selectedEntity, documentId);
    try {
      const response = await fetch(`${API_URL}/api/ged/documents/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSuccess('Document supprime');
        loadDocuments(selectedEntity);
        loadAllEntitiesCounts();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch {
      setError('Erreur lors de la suppression');
    }
  };

  const handleDownload = async (doc) => {
    logAuditAction('download', `Telechargement: ${doc.original_filename || doc.nom}`, selectedEntity, doc.id);
    try {
      if (doc.souscription_id) {
        const token = getToken();
        const res = await fetch(`${API_URL}/api/souscriptions-rcd/${doc.souscription_id}/documents/rcds_unique`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.nom || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        await gedDownload(doc.id, doc.original_filename || doc.nom);
      }
      setSuccess('Telechargement lance...');
      setTimeout(() => setSuccess(''), 2000);
    } catch {
      setError('Erreur lors du telechargement');
    }
  };

  const handlePrint = async (doc) => {
    logAuditAction('print', `Impression: ${doc.original_filename || doc.nom}`, selectedEntity, doc.id);
    setPrintingDocId(doc.id);
    try {
      const token = getToken();
      let url;
      if (doc.souscription_id) {
        url = `${API_URL}/api/souscriptions-rcd/${doc.souscription_id}/documents/rcds_unique`;
      } else {
        url = `${API_URL}/api/ged/download/${doc.id}?mode=view`;
      }
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;opacity:0;pointer-events:none;';
      document.body.appendChild(iframe);

      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch {
          window.open(blobUrl, '_blank');
        }
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
          setPrintingDocId(null);
        }, 1000);
      };

      iframe.onerror = () => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
        setPrintingDocId(null);
        window.open(blobUrl, '_blank');
      };

      iframe.src = blobUrl;
    } catch {
      setPrintingDocId(null);
      setError('Erreur lors de l\'impression');
      setTimeout(() => setError(''), 4000);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Ko';
    const k = 1024;
    const sizes = ['o', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (statut) => {
    const badges = {
      'en_attente': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'En attente', icon: Clock },
      'valide': { color: 'bg-green-100 text-green-800 border-green-200', label: 'Valide', icon: CheckCircle },
      'rejete': { color: 'bg-red-100 text-red-800 border-red-200', label: 'Rejete', icon: XCircle },
      'archive': { color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Archive', icon: Archive }
    };
    const badge = badges[statut] || badges['en_attente'];
    const StatusIcon = badge.icon;
    return (
      <Badge className={cn("text-[10px] border gap-1 px-1.5 py-0", badge.color)}>
        <StatusIcon className="w-3 h-3" />
        {badge.label}
      </Badge>
    );
  };

  const currentEntity = GED_ENTITIES[selectedEntity] || allEntities.find(e => e.id === selectedEntity);

  return {
    userRole,
    selectedEntity, setSelectedEntity,
    documents, documentsByEntity,
    souscriptionDocuments,
    loading, uploading,
    showUploadModal, setShowUploadModal,
    searchQuery, setSearchQuery,
    showSouscriptionDocs, setShowSouscriptionDocs,
    error, setError, success,
    viewMode, setViewMode,
    previewDoc, setPreviewDoc,
    activePanel, setActivePanel,
    connectedData, connectedCounts, connectedLoading,
    connectedSearch, setConnectedSearch,
    docFilters, setDocFilters,
    membresFilters, setMembresFilters,
    ftpeFilters, setFtpeFilters,
    simFilters, setSimFilters,
    customCategories,
    showCategoryManager, setShowCategoryManager,
    avenantSearch, setAvenantSearch,
    avenantFilters, setAvenantFilters,
    complianceData,
    auditEntries,
    auditSearch, setAuditSearch,
    auditFilters, setAuditFilters,
    showAddEntity, setShowAddEntity,
    newEntityForm, setNewEntityForm,
    showInlineNewCat, setShowInlineNewCat,
    newCatLabel, setNewCatLabel,
    publishingDoc,
    isDragging,
    dragFiles, setDragFiles,
    dragPreviews, setDragPreviews,
    printingDocId,
    uploadForm, setUploadForm,
    allEntities, currentEntity, getMergedCategories, filteredDocuments,
    formatFileSize, formatDate, getStatusBadge,
    handleCreateEntity, handlePublishToRegistry, handleInlineCreateCategory,
    handleDragOver, handleDragLeave, handleDrop, handleDragUpload,
    handleUpload, handleView, handleDelete, handleDownload, handlePrint,
    loadCustomCategories, loadCompliance,
  };
}
